import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import API from '../../api';
import Loader from '../../components/Loader';
import { updateRetailer } from '../../actions/Retailer';
import notify from '../../components/Notification/helper';
import { useRefreshToken } from '../../common/hooks/useRefreshToken';
import { chooseInputComponent } from '../../common/utils/components';

// Constants
const FONT_OPTIONS = [
    'Oswald',
    'Roboto',
    'Montserrat',
    'Outfit',
    'Quicksand',
    'Merriweather',
];

const COLOR_OPTIONS = [];

const FORM_SECTIONS = [
    {
        title: '',
        fields: [
            {
                label: 'Label',
                name: 'retailer_name',
                type: 'translation',
                options: [],
            },
        ],
    },
    {
        fields: [
            {
                label: 'Primary Brand Color',
                name: 'brand_color',
                type: 'color',
                options: COLOR_OPTIONS,
            },
            {
                label: 'Secondary Brand Color',
                name: 'brand_color_2',
                type: 'color',
                options: COLOR_OPTIONS,
            },
            {
                label: 'Alternate Brand Color',
                name: 'brand_color_3',
                type: 'color',
                options: COLOR_OPTIONS,
            },
        ],
    },
    {
        title: 'Visual Settings',
        fields: [
            {
                label: 'Logo',
                name: 'image',
                type: 'translation',
                options: [],
            },
        ],
    },
    {
        fields: [
            {
                label: 'Primary Text Color',
                name: 'primary_text_color',
                type: 'color',
                options: COLOR_OPTIONS,
            },
            {
                label: 'Icon Color',
                name: 'icon_color',
                type: 'color',
                options: COLOR_OPTIONS,
            },
            {
                label: 'Background Color',
                name: 'background_color',
                type: 'color',
                options: COLOR_OPTIONS,
            },
        ],
    },
    {
        fields: [
            {
                label: 'Header Font',
                name: 'header_font',
                type: 'select',
                options: FONT_OPTIONS,
            },
            {
                label: 'Body Font',
                name: 'body_font',
                type: 'select',
                options: FONT_OPTIONS,
            },
            {
                label: 'Button Font',
                name: 'button_font',
                type: 'select',
                options: FONT_OPTIONS,
            },
        ],
    },
    {
        title: 'SEO Text',
        fields: [
            {
                label: 'Title',
                name: 'title',
                type: 'translation',
                options: [],
            },
            {
                label: 'Description',
                name: 'description',
                type: 'translation',
                options: [],
            },
            {
                label: 'Keywords',
                name: 'keywords',
                type: 'translation',
                options: [],
            },
        ],
    },
];

function Retailers() {
    useRefreshToken();
    const dispatch = useDispatch();
    const retailer = useSelector((state) => state.retailer);
    const [loading, setLoading] = useState(false);

    // Initialize retailer payload with current retailer data
    const retailerPayload = useMemo(
        () => ({
            translations: retailer?.translations || [],
            image: retailer?.image || '',
        }),
        [retailer?.translations, retailer?.image]
    );

    const handleUpdateRetailer = async () => {
        try {
            setLoading(true);
            const updateRetailerResponse = await API.updateRetailer(retailerPayload);
            notify('success', 'Retailer updated successfully');
            dispatch(updateRetailer(updateRetailerResponse));
        } catch (error) {
            console.error('Failed to update retailer:', error);
            notify('error', 'Failed to update retailer settings');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Box sx={{ flexGrow: 1, my: 3 }}>
            <Typography
                className="form-title"
                variant="h2"
                sx={{ color: 'var(--brand-color, #d32f2f)' }}
            >
                Brand Settings
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                    {FORM_SECTIONS.map((section, sectionIndex) => (
                        <Box key={section.title || `section-${sectionIndex}`}>
                            {section.title && (
                                <Typography
                                    className="section-title"
                                    variant="h2"
                                    sx={{
                                        mt: 5,
                                        mb: 2,
                                    }}
                                >
                                    {section.title}
                                </Typography>
                            )}

                            <Grid container spacing={2}>
                                {section.fields.map((field) =>
                                    chooseInputComponent(field)
                                )}
                            </Grid>
                        </Box>
                    ))}
                </Grid>

                <Grid item xs={12} md={7}>
                    <Button
                        disabled={loading}
                        className="form-button"
                        sx={{
                            width: '40%',
                            my: 5,
                            backgroundColor: 'var(--brand-color, #d32f2f)',
                            '&:hover': {
                                backgroundColor: 'var(--brand-color, #d32f2f)',
                                opacity: 0.9,
                            },
                        }}
                        size="large"
                        variant="contained"
                        onClick={handleUpdateRetailer}
                    >
                        Edit My Brand
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
}

export default Retailers;